import React, { useEffect, useState, useCallback } from 'react'
import styled, { keyframes } from 'styled-components'
import { toast } from 'react-toastify'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined'
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined'
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined'
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined'
import TodayOutlinedIcon from '@mui/icons-material/TodayOutlined'
import DateRangeOutlinedIcon from '@mui/icons-material/DateRangeOutlined'
import NotificationsOffOutlinedIcon from '@mui/icons-material/NotificationsOffOutlined'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import PhonelinkRingOutlinedIcon from '@mui/icons-material/PhonelinkRingOutlined'
import { PRIVATE_PATHS, redirect } from 'utils/paths'
import WebhookOutlinedIcon from '@mui/icons-material/Webhook'
import SearchableSelect from 'components/SearchableSelect'
import WebhookSubscriptionsModal from 'features/compliance/WebhookSubscriptionsModal'
import { getJumpTargets } from 'services/BpService'
import {
  getNotificationPreferences,
  updateNotificationPreferences,
  updateEmailDigestFrequency
} from 'services/notificationService'
import {
  PUSH_PERMISSION,
  getCurrentSubscription,
  getPermissionState,
  isIOS,
  isPushSupported,
  isStandalone,
  subscribeToPush,
  unsubscribeFromPush
} from 'utils/pushNotifications'

const CHANNEL_FIELDS = {
  mail: 'emailEnabled',
  database: 'inAppEnabled',
  push: 'pushEnabled'
}

const FREQUENCY_OPTIONS = [
  {
    value: 'live',
    label: 'Live updates',
    description: 'Sends each email the moment it happens. This can mean several emails in a day.',
    Icon: BoltOutlinedIcon
  },
  {
    value: 'daily',
    label: 'Daily update',
    description: 'One email each morning with everything since yesterday.',
    Icon: TodayOutlinedIcon,
    isDefault: true
  },
  {
    value: 'weekly',
    label: 'Weekly update',
    description: 'One email every Monday morning summarising the week.',
    Icon: DateRangeOutlinedIcon
  },
  {
    value: 'none',
    label: 'No email updates',
    description: 'We will not email you updates. You still see everything in the app under the bell.',
    Icon: NotificationsOffOutlinedIcon
  }
]

const NotificationPreferences = () => {
  const [categories, setCategories] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState({})
  const [emailDigestFrequency, setEmailDigestFrequency] = useState('daily')
  const [frequencySaving, setFrequencySaving] = useState(false)
  const [buildings, setBuildings] = useState([])
  const [buildingsLoading, setBuildingsLoading] = useState(true)
  const [webhookBpId, setWebhookBpId] = useState(null)
  const [webhooksOpen, setWebhooksOpen] = useState(false)

  useEffect(() => {
    let cancelled = false
    getJumpTargets()
      .then(res => {
        if (cancelled) return
        const bps = (res.data?.data || []).filter(t => t.type === 'bp')
        setBuildings(bps)
      })
      .catch(() => { if (!cancelled) setBuildings([]) })
      .finally(() => { if (!cancelled) setBuildingsLoading(false) })
    return () => { cancelled = true }
  }, [])
  const [push, setPush] = useState({
    supported: isPushSupported(),
    permission: getPermissionState(),
    subscribed: false,
    busy: false,
    error: null
  })

  useEffect(() => {
    const load = async () => {
      try {
        const result = await getNotificationPreferences()
        setCategories(result?.data?.data || [])
        if (result?.data?.emailDigestFrequency) {
          setEmailDigestFrequency(result.data.emailDigestFrequency)
        }
      } catch (error) {
        console.error('Failed to load notification preferences:', error)
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [])

  useEffect(() => {
    let cancelled = false
    const checkDevice = async () => {
      try {
        const subscription = await getCurrentSubscription()
        if (!cancelled) {
          setPush(prev => ({
            ...prev,
            permission: getPermissionState(),
            subscribed: Boolean(subscription)
          }))
        }
      } catch (error) {
        // Treat as not subscribed; the user can still try to enable.
      }
    }
    checkDevice()
    return () => {
      cancelled = true
    }
  }, [])

  const handleToggle = useCallback(async (code, channel, currentEnabled) => {
    const newEnabled = !currentEnabled
    const field = CHANNEL_FIELDS[channel]
    const savingKey = `${code}:${channel}`

    // Optimistic update
    setCategories(prev =>
      prev.map(cat => ({
        ...cat,
        items: cat.items.map(item =>
          item.code === code ? { ...item, [field]: newEnabled } : item
        )
      }))
    )

    setSaving(prev => ({ ...prev, [savingKey]: true }))

    try {
      await updateNotificationPreferences([
        { code, channel, enabled: newEnabled }
      ])
    } catch (error) {
      // Revert on failure
      setCategories(prev =>
        prev.map(cat => ({
          ...cat,
          items: cat.items.map(item =>
            item.code === code ? { ...item, [field]: currentEnabled } : item
          )
        }))
      )
      console.error('Failed to update preference:', error)
    } finally {
      setSaving(prev => ({ ...prev, [savingKey]: false }))
    }
  }, [])

  const handleFrequencyChange = useCallback(async (value, previousValue) => {
    if (value === previousValue) return

    setEmailDigestFrequency(value)
    setFrequencySaving(true)

    try {
      await updateEmailDigestFrequency(value)
      toast.success('Email frequency updated.')
    } catch (error) {
      setEmailDigestFrequency(previousValue)
      console.error('Failed to update email digest frequency:', error)
      toast.error('Could not update your email frequency. Please try again.')
    } finally {
      setFrequencySaving(false)
    }
  }, [])

  const handleEnablePush = useCallback(async () => {
    setPush(prev => ({ ...prev, busy: true, error: null }))
    try {
      const result = await subscribeToPush()
      setPush(prev => ({
        ...prev,
        busy: false,
        permission: result.status,
        subscribed: result.status === PUSH_PERMISSION.GRANTED,
        error: result.status === PUSH_PERMISSION.GRANTED
          ? null
          : 'Notifications were not allowed. You can change this in your browser settings.'
      }))
    } catch (error) {
      console.error('Failed to enable push notifications:', error)
      const message = error?.code === 'no-service-worker'
        ? 'Push notifications are only available in the installed app or the production build.'
        : error?.code === 'no-public-key'
          ? 'Push notifications are not configured on the server yet.'
          : 'Could not enable push notifications on this device.'
      setPush(prev => ({ ...prev, busy: false, permission: getPermissionState(), error: message }))
    }
  }, [])

  const handleDisablePush = useCallback(async () => {
    setPush(prev => ({ ...prev, busy: true, error: null }))
    try {
      await unsubscribeFromPush()
      setPush(prev => ({ ...prev, busy: false, subscribed: false }))
    } catch (error) {
      console.error('Failed to disable push notifications:', error)
      setPush(prev => ({ ...prev, busy: false, error: 'Could not disable push notifications on this device.' }))
    }
  }, [])

  const renderDeviceCard = () => {
    const iosNeedsInstall = isIOS() && !isStandalone()

    let status
    let hint
    let action = null

    if (!push.supported || iosNeedsInstall) {
      status = 'Not available in this browser'
      hint = iosNeedsInstall
        ? 'On iPhone and iPad, add Building Passport to your Home Screen first (Share → Add to Home Screen), then enable push from the installed app.'
        : 'This browser does not support push notifications. Try the installed app or a recent version of Chrome, Edge, Firefox or Safari.'
    } else if (push.permission === PUSH_PERMISSION.DENIED) {
      status = 'Blocked'
      hint = 'Notifications are blocked for Building Passport. Allow them in your browser or system settings, then reload this page.'
    } else if (push.subscribed) {
      status = 'Enabled on this device'
      hint = 'You will receive push notifications here for the types selected below, even when the app is closed.'
      action = (
        <DeviceButton $variant="secondary" onClick={handleDisablePush} disabled={push.busy}>
          {push.busy ? 'Disabling…' : 'Disable on this device'}
        </DeviceButton>
      )
    } else {
      status = 'Off on this device'
      hint = 'Get notified on this device even when Building Passport is closed. Your browser will ask for permission.'
      action = (
        <DeviceButton onClick={handleEnablePush} disabled={push.busy}>
          {push.busy ? 'Enabling…' : 'Enable on this device'}
        </DeviceButton>
      )
    }

    return (
      <DeviceCard>
        <DeviceIcon $active={push.subscribed}><PhonelinkRingOutlinedIcon /></DeviceIcon>
        <DeviceContent>
          <DeviceTitle>
            Push notifications
            <DeviceStatus $active={push.subscribed}>{status}</DeviceStatus>
          </DeviceTitle>
          <DeviceHint>{hint}</DeviceHint>
          {push.error && <DeviceError>{push.error}</DeviceError>}
        </DeviceContent>
        {action && <DeviceActions>{action}</DeviceActions>}
      </DeviceCard>
    )
  }

  if (loading) {
    return (
      <PageWrapper>
        <ContentContainer>
          <LoadingState>Loading preferences...</LoadingState>
        </ContentContainer>
      </PageWrapper>
    )
  }

  return (
    <PageWrapper>
      <ContentContainer>
        <TopBar>
          <BackButton onClick={() => redirect(PRIVATE_PATHS.dashboard)}>
            <ArrowBackIcon />
            <span>Back</span>
          </BackButton>
        </TopBar>

        <PageHeader>
          <HeaderIcon><NotificationsOutlinedIcon /></HeaderIcon>
          <div>
            <PageTitle>Notification Preferences</PageTitle>
            <PageSubtitle>Choose which notifications you want to receive via email, in-app and push.</PageSubtitle>
          </div>
        </PageHeader>

        <FrequencyPanel data-tour-id='notifications.frequency'>
          <FrequencyPanelTitle>How often should we email you?</FrequencyPanelTitle>
          <FrequencyPanelHelp>Everything that would normally email you is gathered into a single update.</FrequencyPanelHelp>
          <FrequencyOptions role='radiogroup' aria-label='Email frequency'>
            {FREQUENCY_OPTIONS.map(({ value, label, description, Icon, isDefault }) => {
              const selected = emailDigestFrequency === value
              return (
                <FrequencyOption
                  key={value}
                  type='button'
                  role='radio'
                  aria-checked={selected}
                  $selected={selected}
                  disabled={frequencySaving}
                  onClick={() => handleFrequencyChange(value, emailDigestFrequency)}
                >
                  <RadioDot $selected={selected} />
                  <FrequencyIconWrap $selected={selected}>
                    <Icon />
                  </FrequencyIconWrap>
                  <FrequencyText>
                    <FrequencyName>
                      {label}
                      {isDefault && <DefaultTag>Default</DefaultTag>}
                    </FrequencyName>
                    <FrequencySub>{description}</FrequencySub>
                  </FrequencyText>
                </FrequencyOption>
              )
            })}
          </FrequencyOptions>
          <FrequencyNote>
            <InfoOutlinedIcon />
            <span>Security and access emails always send straight away, whatever you choose here.</span>
          </FrequencyNote>
        </FrequencyPanel>
        {renderDeviceCard()}

        {categories.map(category => (
          <CategoryCard key={category.category} data-tour-id='notifications.category'>
            <CategoryHeader>
              <CategoryLabel>{category.label}</CategoryLabel>
              <ChannelHeaders>
                <ChannelHeader>
                  <EmailOutlinedIcon />
                  <span>Email</span>
                </ChannelHeader>
                <ChannelHeader>
                  <NotificationsActiveOutlinedIcon />
                  <span>In-App</span>
                </ChannelHeader>
                <ChannelHeader>
                  <PhonelinkRingOutlinedIcon />
                  <span>Push</span>
                </ChannelHeader>
              </ChannelHeaders>
            </CategoryHeader>
            <CategoryItems>
              {category.items.map(item => (
                <PreferenceRow key={item.code}>
                  <PreferenceLabel>{item.label}</PreferenceLabel>
                  <ToggleGroup>
                    {item.emailSupported === false ? (
                      <TogglePlaceholder title="Not available by email">·</TogglePlaceholder>
                    ) : (
                      <ToggleSwitch
                        $active={item.emailEnabled}
                        $saving={saving[`${item.code}:mail`]}
                        onClick={() => handleToggle(item.code, 'mail', item.emailEnabled)}
                        title="Email notifications"
                      >
                        <ToggleKnob $active={item.emailEnabled} />
                      </ToggleSwitch>
                    )}
                    {item.inAppSupported === false ? (
                      <TogglePlaceholder title="Not available in-app">·</TogglePlaceholder>
                    ) : (
                      <ToggleSwitch
                        $active={item.inAppEnabled}
                        $saving={saving[`${item.code}:database`]}
                        onClick={() => handleToggle(item.code, 'database', item.inAppEnabled)}
                        title="In-app notifications"
                      >
                        <ToggleKnob $active={item.inAppEnabled} />
                      </ToggleSwitch>
                    )}
                    {item.pushSupported === false ? (
                      <TogglePlaceholder title="Not available as push">·</TogglePlaceholder>
                    ) : (
                      <ToggleSwitch
                        $active={item.pushEnabled}
                        $saving={saving[`${item.code}:push`]}
                        onClick={() => handleToggle(item.code, 'push', item.pushEnabled)}
                        title="Push notifications"
                      >
                        <ToggleKnob $active={item.pushEnabled} />
                      </ToggleSwitch>
                    )}
                  </ToggleGroup>
                </PreferenceRow>
              ))}
            </CategoryItems>
          </CategoryCard>
        ))}
        <CategoryCard>
          <CategoryHeader>
            <CategoryLabel>Webhooks</CategoryLabel>
          </CategoryHeader>
          <WebhookBody>
            <WebhookIntro>
              A webhook sends a message to another app. Slack, Microsoft Teams, or any address that
              accepts HTTPS. Whenever something happens on one of your buildings. Pick the building
              you want to send messages about, then choose which events to send.
            </WebhookIntro>
            <WebhookRow>
              <SearchableSelect
                ariaLabel='Building Passport'
                value={webhookBpId}
                onChange={setWebhookBpId}
                options={buildings.map(b => ({ value: b.id, label: b.name }))}
                placeholder={buildingsLoading ? 'Loading your buildings…' : 'Choose a Building Passport…'}
                searchPlaceholder='Search your buildings…'
                disabled={buildingsLoading || buildings.length === 0}
              />
              <ManageWebhooksBtn
                type='button'
                disabled={!webhookBpId}
                onClick={() => setWebhooksOpen(true)}
                title={webhookBpId ? 'Manage webhooks for this building' : 'Choose a building first'}
              >
                <WebhookOutlinedIcon style={{ fontSize: 17 }} />
                Manage webhooks
              </ManageWebhooksBtn>
            </WebhookRow>
            {!buildingsLoading && buildings.length === 0 && (
              <WebhookEmpty>You do not have any Building Passports yet, so there is nothing to send messages about.</WebhookEmpty>
            )}
          </WebhookBody>
        </CategoryCard>
      </ContentContainer>

      {webhooksOpen && webhookBpId && (
        <WebhookSubscriptionsModal
          bpId={webhookBpId}
          onClose={() => setWebhooksOpen(false)}
        />
      )}
    </PageWrapper>
  )
}

export default NotificationPreferences

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`

const PageWrapper = styled.div`
  position: fixed;
  top: ${({ theme }) => theme.dimensions.topBarHeight};
  left: ${({ theme }) => theme.dimensions.navWidth};
  right: 0;
  bottom: 0;
  background: ${({ theme }) => theme.colors.backgroundColor};
  overflow-y: auto;
  animation: ${fadeIn} 0.3s ease-out;

  @media (max-width: 768px) {
    left: 0;
    top: 60px;
  }
`

const ContentContainer = styled.div`
  max-width: 720px;
  margin: 0 auto;
  padding: 32px 24px;

  @media (max-width: 768px) {
    padding: 20px 16px;
  }
`

const TopBar = styled.div`
  margin-bottom: 24px;
`

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: transparent;
  border: 1px solid ${({ theme }) => theme.colors.lightGrey};
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.haiti};
  cursor: pointer;
  transition: all 0.2s;

  svg { font-size: 20px; }

  &:hover {
    background: white;
    border-color: ${({ theme }) => theme.colors.haiti};
  }
`

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
`

const HeaderIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: ${({ theme }) => theme.colors.haiti};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;

  svg { font-size: 24px; }
`

const PageTitle = styled.h1`
  font-size: 20px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.haiti};
  margin: 0 0 4px 0;
`

const PageSubtitle = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#999'};
  margin: 0;
`

const LoadingState = styled.div`
  text-align: center;
  padding: 60px 0;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#999'};
`

const FrequencyPanel = styled.div`
  background: white;
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.colors.lightGrey};
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
  padding: 20px;
  margin-bottom: 16px;
`

const FrequencyPanelTitle = styled.h2`
  font-size: 15px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.haiti};
  margin: 0 0 4px 0;
`

const FrequencyPanelHelp = styled.p`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#999'};
  margin: 0 0 16px 0;
`

const FrequencyOptions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const FrequencyOption = styled.button`
  display: flex;
  align-items: flex-start;
  gap: 13px;
  width: 100%;
  min-height: 64px;
  padding: 14px;
  border: 1.5px solid ${({ theme, $selected }) => ($selected ? theme.colors.secondary : theme.colors.lightGrey)};
  border-radius: 10px;
  background: ${({ $selected }) => ($selected ? '#e6f7f9' : 'transparent')};
  text-align: left;
  font-family: inherit;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;

  &:hover {
    border-color: ${({ theme, $selected }) => ($selected ? theme.colors.secondary : theme.colors.grey?.[700] || '#b3b3b3')};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.7;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.secondary};
    outline-offset: 2px;
  }

  @media (max-width: 480px) {
    padding: 12px;
    gap: 10px;
  }
`

const RadioDot = styled.span`
  flex: 0 0 auto;
  width: 20px;
  height: 20px;
  margin-top: 1px;
  border-radius: 50%;
  border: 2px solid ${({ theme, $selected }) => ($selected ? theme.colors.secondary : theme.colors.mediumGrey || '#d1d5db')};
  display: flex;
  align-items: center;
  justify-content: center;

  &::after {
    content: '';
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: ${({ theme, $selected }) => ($selected ? theme.colors.secondary : 'transparent')};
  }
`

const FrequencyIconWrap = styled.span`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  margin-top: 1px;
  color: ${({ $selected }) => ($selected ? '#1f8c97' : '#999')};

  svg { font-size: 22px; }
`

const FrequencyText = styled.span`
  min-width: 0;
`

const FrequencyName = styled.span`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  font-size: 14px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.haiti};
`

const DefaultTag = styled.span`
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #1f8c97;
  background: #e6f7f9;
  border: 1px solid #33becc;
  padding: 2px 7px;
  border-radius: 999px;
`

const FrequencySub = styled.span`
  display: block;
  font-size: 12.5px;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#999'};
  margin-top: 2px;
`

const FrequencyNote = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-top: 16px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#999'};

  svg {
    flex: 0 0 auto;
    margin-top: 1px;
    font-size: 15px;
    color: #1f8c97;
  }
`

const DeviceCard = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  background: white;
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.colors.lightGrey};
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
  padding: 16px 20px;
  margin-bottom: 24px;

  @media (max-width: 600px) {
    flex-wrap: wrap;
  }
`

const DeviceIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ $active, theme }) =>
    $active ? theme.colors.secondary || '#33becc' : theme.colors.lightGrey};
  color: ${({ $active, theme }) => ($active ? 'white' : theme.colors.haiti)};

  svg { font-size: 22px; }
`

const DeviceContent = styled.div`
  flex: 1;
  min-width: 0;
`

const DeviceTitle = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  font-size: 15px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.haiti};
`

const DeviceStatus = styled.span`
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  padding: 2px 8px;
  border-radius: 999px;
  background: ${({ $active, theme }) =>
    $active ? 'rgba(51, 190, 204, 0.15)' : theme.colors.lightGrey};
  color: ${({ $active, theme }) =>
    $active ? theme.colors.secondary || '#33becc' : theme.colors.grey?.[800] || '#666'};
`

const DeviceHint = styled.p`
  margin: 6px 0 0;
  font-size: 13px;
  line-height: 1.45;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#666'};
`

const DeviceError = styled.p`
  margin: 8px 0 0;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.error || '#d32f2f'};
`

const DeviceActions = styled.div`
  flex-shrink: 0;
  align-self: center;

  @media (max-width: 600px) {
    width: 100%;
  }
`

const DeviceButton = styled.button`
  padding: 9px 16px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid ${({ $variant, theme }) =>
    $variant === 'secondary' ? theme.colors.lightGrey : theme.colors.secondary || '#33becc'};
  background: ${({ $variant, theme }) =>
    $variant === 'secondary' ? 'transparent' : theme.colors.secondary || '#33becc'};
  color: ${({ $variant, theme }) =>
    $variant === 'secondary' ? theme.colors.haiti : 'white'};

  &:hover:not(:disabled) {
    opacity: 0.85;
  }

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }

  @media (max-width: 600px) {
    width: 100%;
  }
`

const CategoryCard = styled.div`
  background: white;
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.colors.lightGrey};
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
  margin-bottom: 16px;
  overflow: hidden;
`

const CategoryHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.lightGrey};
  background: ${({ theme }) => theme.colors.lightGrey};
`

const CategoryLabel = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.haiti};
`

const ChannelHeaders = styled.div`
  display: flex;
  gap: 16px;
`

const ChannelHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#999'};
  width: 52px;
  justify-content: center;

  svg { font-size: 14px; }

  span {
    @media (max-width: 480px) {
      display: none;
    }
  }
`

const CategoryItems = styled.div`
  padding: 4px 0;
`

const PreferenceRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  gap: 16px;

  &:not(:last-child) {
    border-bottom: 1px solid ${({ theme }) => theme.colors.lightGrey};
  }
`

const PreferenceLabel = styled.span`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.haiti};
  flex: 1;
  min-width: 0;
`

const ToggleGroup = styled.div`
  display: flex;
  gap: 16px;
  flex-shrink: 0;
`

const ToggleSwitch = styled.div`
  width: 44px;
  height: 24px;
  border-radius: 12px;
  background: ${({ $active, theme }) =>
    $active ? theme.colors.secondary || '#33becc' : theme.colors.mediumGrey || '#d1d5db'};
  cursor: pointer;
  position: relative;
  transition: background 0.2s;
  opacity: ${({ $saving }) => ($saving ? 0.6 : 1)};
  pointer-events: ${({ $saving }) => ($saving ? 'none' : 'auto')};
  flex-shrink: 0;
`

const TogglePlaceholder = styled.div`
  width: 44px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.mediumGrey || '#d1d5db'};
  font-size: 18px;
  flex-shrink: 0;
`

const ToggleKnob = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: white;
  position: absolute;
  top: 2px;
  left: ${({ $active }) => ($active ? '22px' : '2px')};
  transition: left 0.2s;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
`

const WebhookBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 18px 20px 20px;
`

const WebhookIntro = styled.p`
  margin: 0;
  max-width: 68ch;
  font-size: 14px;
  line-height: 1.5;
  color: #4A4F5E;
`

const WebhookRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;

  > *:first-child {
    flex: 1;
    min-width: 240px;
  }
`

const ManageWebhooksBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 42px;
  padding: 0 16px;
  border: 1px solid #33becc;
  border-radius: 8px;
  background: #e6f7f9;
  color: #1f8c97;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 160ms ease;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:not(:disabled):hover {
    background: #d5f1f4;
  }
`

const WebhookEmpty = styled.p`
  margin: 0;
  font-size: 13px;
  color: #8A8F9C;
`
